import { Box, Chip, CircularProgress, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { getResource } from "../../data/request";
import { getDateDisplay, getValueDisplay } from "../../data/utilities";
import { Charge } from "../../types/Charge";
import { STATUS_AMBER, STATUS_GREEN, STATUS_GREY, TEXT_SECONDARY } from "../colors";

// Real Companies House values: "outstanding", "part-satisfied", "fully-satisfied"
// (the previous check compared against "satisfied", which never matches the
// real "fully-satisfied" value, so every charge rendered green regardless).
const chargeStatusColor = (status: string | undefined): string => {
  if (status === "fully-satisfied") return STATUS_GREEN;
  if (status === "part-satisfied") return STATUS_AMBER;
  return STATUS_GREY; // outstanding, or anything unrecognized - a normal, not alarming, state
};

export const ChargesTable = (props: { link: string }) => {
  const { link } = props;
  const [charges, setCharges] = useState<Charge[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const doc = await getResource(link);
      setCharges(doc?.items ?? []);
      setLoading(false);
    };

    fetchData();
  }, [link]);

  if (loading) {
    return (
      <Box
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: 200,
        }}
      >
        <CircularProgress color="primary" />
      </Box>
    );
  }

  if (charges.length === 0) {
    return (
      <Box style={{ padding: 20 }}>
        <Typography variant="body2" style={{ color: TEXT_SECONDARY }}>
          No charges (mortgages/debentures) recorded for this company.
        </Typography>
      </Box>
    );
  }

  return (
    <Box style={{ padding: 20 }}>
      {charges.map((charge, index) => (
        <Box
          key={charge.charge_number ?? index}
          style={{ padding: 16, marginBottom: 12, borderBottom: "1px solid #E2E2E7" }}
        >
          <Box style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8 }}>
            <Typography variant="subtitle1">
              {getValueDisplay(charge.classification?.description ?? charge.classification?.type)}
            </Typography>
            <Chip
              label={getValueDisplay(charge.status)}
              size="small"
              style={{
                backgroundColor: chargeStatusColor(charge.status),
                color: "white",
              }}
            />
          </Box>
          <Typography variant="body2" style={{ color: TEXT_SECONDARY }}>
            Created: {getDateDisplay(charge.created_on)} | Delivered:{" "}
            {getDateDisplay(charge.delivered_on)}
            {charge.satisfied_on && ` | Satisfied: ${getDateDisplay(charge.satisfied_on)}`}
          </Typography>
          {charge.particulars?.description && (
            <Typography variant="body2" style={{ marginTop: 8 }}>
              {charge.particulars.description}
            </Typography>
          )}
          {charge.persons_entitled && charge.persons_entitled.length > 0 && (
            <Typography variant="body2" style={{ marginTop: 8, color: TEXT_SECONDARY }}>
              Persons entitled: {charge.persons_entitled.map((p) => p.name).join(", ")}
            </Typography>
          )}
        </Box>
      ))}
    </Box>
  );
};

export default ChargesTable;
